import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Tabs } from '@syneto/compass-react';
import ActionModal from '../../../../components/ActionModal';
import RenderOnlyActiveTab from '../../../../components/RenderOnlyActiveTab';
import { HARDWARE_INVENTORY_TABS, HARDWARE_INVENTORY_ACTIVE_TAB_KEY, NOT_AVAILABLE } from '../../../../constants/hardware-inventory';
import {
	createFirmwareAdvisory,
	hideFirmwareAdvisoryFormModal,
	showSeededFirmwareAdvisoryFormModal
} from '../../../../data/firmwareAdvisory/actions';
import { advisorySeedFromApproval } from '../../../../data/firmwareAdvisory/mapper';
import {
	APPROVAL_WRITE_OUTCOMES,
	approveComponentTypeFirmwareVersion,
	getComponentTypes,
	getFleetComponents,
	hideComponentTypeApprovalModal,
	showComponentTypeApprovalModal
} from '../../../../data/hardwareInventory/actions';
import { formValuesToApprovalPayload } from '../../../../data/hardwareInventory/mapper';
import FirmwareAdvisoryFormModal from '../AdminFirmwareAdvisories/firmwareAdvisoryFormModal';
import ApproveFirmwareVersionModal from './approveFirmwareVersionModal';
import HardwareComponentTypesTable from './hardwareComponentTypesTable';
import FleetInventoryTable from './fleetInventoryTable';
import { useFleetInventoryFilters } from './useFleetInventoryFilters';

const AdminHardwareInventory = (props) => {
	const {
		components,
		pagination,
		componentsLoading,
		componentsError,
		componentTypes,
		componentTypesLoading,
		componentTypesError,
		savingApproval,
		componentTypeToApprove,
		approvedBy,
		showAdvisoryFormModal,
		advisorySeed,
		getFleetComponents,
		getComponentTypes,
		approveComponentTypeFirmwareVersion,
		openApprovalModal,
		hideApprovalModal,
		createFirmwareAdvisory,
		openSeededAdvisoryFormModal,
		hideAdvisoryFormModal
	} = props;

	const [activeKey, setActiveKey] = useState(
		localStorage.getItem(HARDWARE_INVENTORY_ACTIVE_TAB_KEY) || HARDWARE_INVENTORY_TABS.COMPONENT_TYPES
	);

	/*
	 * The approval that just applied, while the operator is asked whether to add an advisory for it.
	 * Local rather than in the store: nothing outside this page reacts to it, and it has to disappear
	 * with the page.
	 */
	const [advisoryPrompt, setAdvisoryPrompt] = useState(null);

	const showFleetTab = () => {
		setActiveKey(HARDWARE_INVENTORY_TABS.COMPONENTS);
	};

	const {
		filters,
		smartQuery,
		smartSearch,
		queryParams,
		selectedComponentType,
		onFilterChange,
		onSmartQueryChange,
		onClearAll,
		onPaginationChange,
		onSelectComponentType
	} = useFleetInventoryFilters(componentTypes, showFleetTab);

	/*
	 * An approval outlives its request: it goes on to poll a job for up to a minute. Navigating away
	 * mid-poll has to stop it, otherwise it dispatches into an unmounted page and pushes a
	 * notification about work nobody is watching any more.
	 */
	const abortRef = useRef(null);

	useEffect(() => {
		abortRef.current = new AbortController();

		return () => {
			abortRef.current?.abort();
			/*
			 * The advisory form's open flag lives in the store, so a form left open here would
			 * otherwise reappear the next time the Firmware Advisories page is visited.
			 */
			hideAdvisoryFormModal();
		};
	}, []);

	useEffect(() => {
		localStorage.setItem(HARDWARE_INVENTORY_ACTIVE_TAB_KEY, activeKey);
	}, [activeKey]);

	/*
	 * The analytics rows are fetched once for both tabs: the firmware tab tabulates them, the fleet
	 * tab's detail modal reads them for a component's fleet context, and the search box resolves
	 * what you type against them.
	 */
	useEffect(() => {
		getComponentTypes();
	}, []);

	useEffect(() => {
		getFleetComponents(queryParams);
	}, [queryParams]);

	/*
	 * The prompt is offered only when the approval is confirmed applied - never after a failed,
	 * abandoned or unconfirmed write - and it carries the submitted version rather than one re-read
	 * from the refreshed catalog, which can still lag (see `advisorySeedFromApproval`). The action
	 * resolves only after closing the approval modal and re-reading the table, so the two dialogs
	 * never stack.
	 */
	const onSubmitApproval = (values) => {
		const componentType = componentTypeToApprove;
		const approvedVersion = formValuesToApprovalPayload(values, approvedBy).approvedFirmwareVersion;

		return approveComponentTypeFirmwareVersion(componentType.id, values, {
			approvedBy: approvedBy,
			signal: abortRef.current?.signal
		}).then((outcome) => {
			if (outcome === APPROVAL_WRITE_OUTCOMES.APPLIED && !abortRef.current?.signal.aborted) {
				setAdvisoryPrompt({ componentType: componentType, approvedVersion: approvedVersion });
			}

			return outcome;
		});
	};

	const dismissAdvisoryPrompt = () => {
		setAdvisoryPrompt(null);
	};

	const acceptAdvisoryPrompt = () => {
		openSeededAdvisoryFormModal(advisorySeedFromApproval(advisoryPrompt.componentType, advisoryPrompt.approvedVersion));
		setAdvisoryPrompt(null);
	};

	const onSubmitAdvisory = (values) => {
		return createFirmwareAdvisory(values, { signal: abortRef.current?.signal });
	};

	return (
		<div id="hardware-inventory" className="p-4 bg-white w-100">
			<div className="col-12">
				<Tabs activeKey={activeKey} onSelect={(tab) => { return setActiveKey(tab); }}>
					<Tabs.Tab
						eventKey={HARDWARE_INVENTORY_TABS.COMPONENT_TYPES}
						title="Firmware by Type"
						className="qa-component-types"
					>
						<RenderOnlyActiveTab tabKey={HARDWARE_INVENTORY_TABS.COMPONENT_TYPES} tabsActiveKey={activeKey}>
							{componentTypesError && <Alert variant="danger">{componentTypesError}</Alert>}

							{/*
							  * `approvedBy` lands in an audit column, and the profile loads
							  * asynchronously, so the session email can legitimately be missing for a
							  * moment. Disabling the action is better than a rejected write whose
							  * message is about a field the operator never filled in.
							  */}
							{!approvedBy && (
								<Alert variant="warning">
									Your account details are still loading. Approving firmware versions is unavailable
									until they arrive, because every approval records who made it.
								</Alert>
							)}

							<HardwareComponentTypesTable
								componentTypes={componentTypes}
								isLoading={componentTypesLoading}
								hasError={Boolean(componentTypesError)}
								isSaving={savingApproval}
								canApprove={Boolean(approvedBy)}
								onSelectComponentType={onSelectComponentType}
								onApproveComponentType={openApprovalModal}
							/>
						</RenderOnlyActiveTab>
					</Tabs.Tab>

					<Tabs.Tab
						eventKey={HARDWARE_INVENTORY_TABS.COMPONENTS}
						title="Fleet Inventory"
						className="qa-fleet-inventory"
					>
						<RenderOnlyActiveTab tabKey={HARDWARE_INVENTORY_TABS.COMPONENTS} tabsActiveKey={activeKey}>
							{componentsError && <Alert variant="danger">{componentsError}</Alert>}

							<FleetInventoryTable
								components={components}
								pagination={pagination}
								isLoading={componentsLoading}
								hasError={Boolean(componentsError)}
								componentTypes={componentTypes}
								filters={filters}
								smartQuery={smartQuery}
								smartSearch={smartSearch}
								selectedComponentType={selectedComponentType}
								onFilterChange={onFilterChange}
								onSmartQueryChange={onSmartQueryChange}
								onClearAll={onClearAll}
								onPaginationChange={onPaginationChange}
							/>
						</RenderOnlyActiveTab>
					</Tabs.Tab>
				</Tabs>

				{componentTypeToApprove && (
					<ApproveFirmwareVersionModal
						componentType={componentTypeToApprove}
						approvedBy={approvedBy}
						submitting={savingApproval}
						onSubmitApproval={onSubmitApproval}
						onCancel={hideApprovalModal}
					/>
				)}

				<ActionModal
					showModal={Boolean(advisoryPrompt)}
					onHide={dismissAdvisoryPrompt}
					callback={acceptAdvisoryPrompt}
					primaryLabel="Add advisory"
					title="Add a firmware advisory?"
					content={advisoryPrompt && (
						<p className="mb-0">
							<span className="text-value">{advisoryPrompt.approvedVersion}</span> is now the approved
							version for <span className="text-value">{advisoryPrompt.componentType.displayName ?? NOT_AVAILABLE}</span>.
							Do you want to add a firmware advisory for this component type, with this version as its target?
						</p>
					)}
				/>

				{showAdvisoryFormModal && (
					<FirmwareAdvisoryFormModal
						advisory={null}
						seed={advisorySeed}
						componentTypes={componentTypes}
						componentTypesLoading={componentTypesLoading}
						onSubmitAdvisory={onSubmitAdvisory}
						onCancel={hideAdvisoryFormModal}
					/>
				)}
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		components: state.hardwareInventory.components,
		pagination: state.hardwareInventory.pagination,
		componentsLoading: state.hardwareInventory.componentsLoading,
		componentsError: state.hardwareInventory.componentsError,
		componentTypes: state.hardwareInventory.componentTypes,
		componentTypesLoading: state.hardwareInventory.componentTypesLoading,
		componentTypesError: state.hardwareInventory.componentTypesError,
		savingApproval: state.hardwareInventory.savingApproval,
		componentTypeToApprove: state.hardwareInventory.componentTypeToApprove,
		/*
		 * The identity recorded on every approval. Taken from the session rather than typed, so an
		 * operator cannot attribute an endorsement to someone else.
		 */
		approvedBy: state.user?.user?.account?.email ?? '',
		showAdvisoryFormModal: state.firmwareAdvisories.showFormModal,
		advisorySeed: state.firmwareAdvisories.advisorySeed
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		getFleetComponents: (queryParams) => { return dispatch(getFleetComponents(queryParams)); },
		getComponentTypes: () => { return dispatch(getComponentTypes()); },
		approveComponentTypeFirmwareVersion: (id, values, options) => {
			return dispatch(approveComponentTypeFirmwareVersion(id, values, options));
		},
		openApprovalModal: (componentType) => { return dispatch(showComponentTypeApprovalModal(componentType)); },
		hideApprovalModal: () => { return dispatch(hideComponentTypeApprovalModal()); },
		createFirmwareAdvisory: (values, options) => { return dispatch(createFirmwareAdvisory(values, options)); },
		openSeededAdvisoryFormModal: (seed) => { return dispatch(showSeededFirmwareAdvisoryFormModal(seed)); },
		hideAdvisoryFormModal: () => { return dispatch(hideFirmwareAdvisoryFormModal()); }
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminHardwareInventory);
